/**
 * Overlay Editor
 *
 * Edit a channel's overlay: its name/enabled state and per-alert-type variants
 * (template, duration, style, match condition), with a test-fire control that
 * runs a synthetic event through the real pipeline (preview == live).
 *
 * Route: /channels/:channelId/overlays/:overlayId
 *
 * MVP scope: alert overlays. Variant style/condition expose the common fields
 * as inputs plus a raw-JSON escape hatch for anything else, rather than guessing
 * every possible key. Styling: synthwave design system (semantic tokens).
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiErrorMessage } from '@/api/client'
import { useToast } from '@/components/Toast'
import {
  getOverlay,
  updateOverlay,
  listVariants,
  createVariant,
  updateVariant,
  deleteVariant,
  testFire,
  type Overlay,
  type AlertVariant,
} from '@/api/overlaysApi'
import {
  Save,
  Plus,
  Trash2,
  Play,
  AlertCircle,
  Loader2,
} from 'lucide-react'
import clsx from 'clsx'

const ALERT_TYPES = ['follow', 'subscribe', 'cheer', 'raid', 'redemption'] as const

/** Test-fire field presets per alert type, so the preview button "just works". */
const TEST_PRESETS: Record<string, { capability: string; fields: Record<string, unknown> }> = {
  follow: { capability: 'core.follow', fields: { follower_name: 'TestViewer' } },
  subscribe: {
    capability: 'core.subscribe',
    fields: { viewer_name: 'TestViewer', tier: '1000' },
  },
  cheer: {
    capability: 'core.cheer',
    fields: { viewer_name: 'TestViewer', bits: 500, anonymous: false },
  },
  raid: { capability: 'core.raid', fields: { from_name: 'TestRaider', viewers: 42 } },
  redemption: {
    capability: 'core.redemption',
    fields: { viewer_name: 'TestViewer', reward: 'Test Reward' },
  },
}

/** Small labelled text input. */
function Field({
  label,
  value,
  onChange,
  placeholder,
  hint,
  type = 'text',
}: {
  label: string
  value: string | number
  onChange: (v: string) => void
  placeholder?: string
  hint?: string
  type?: string
}) {
  return (
    <div>
      <label className="block text-sm text-text-secondary mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="input"
      />
      {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
    </div>
  )
}

/** A JSON textarea with parse validation; reports validity to the parent. */
function JsonField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: Record<string, unknown>
  onChange: (v: Record<string, unknown>, valid: boolean) => void
  hint?: string
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2))
  const [valid, setValid] = useState(true)

  // Re-sync if the underlying object identity changes (e.g. switching variants).
  useEffect(() => {
    setText(JSON.stringify(value, null, 2))
    setValid(true)
  }, [value])

  const handle = (t: string) => {
    setText(t)
    try {
      const parsed = t.trim() === '' ? {} : JSON.parse(t)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setValid(true)
        onChange(parsed as Record<string, unknown>, true)
      } else {
        setValid(false)
        onChange(value, false)
      }
    } catch {
      setValid(false)
      onChange(value, false)
    }
  }

  return (
    <div>
      <label className="block text-sm text-text-secondary mb-2">{label}</label>
      <textarea
        value={text}
        onChange={(e) => handle(e.target.value)}
        rows={4}
        className={clsx('input font-mono text-sm', !valid && 'border-state-danger')}
        spellCheck={false}
      />
      {!valid ? (
        <p className="text-xs text-state-danger mt-1">Invalid JSON — changes won't save until fixed.</p>
      ) : (
        hint && <p className="text-xs text-text-muted mt-1">{hint}</p>
      )}
    </div>
  )
}

/** Editor for a single alert variant. */
function VariantEditor({
  variant,
  onSaved,
  onDeleted,
}: {
  variant: AlertVariant
  onSaved: (v: AlertVariant) => void
  onDeleted: (id: number) => void
}) {
  const { success, error } = useToast()
  const [template, setTemplate] = useState(variant.template)
  const [duration, setDuration] = useState(String(variant.duration_ms))
  const [style, setStyle] = useState<Record<string, unknown>>(variant.style)
  const [condition, setCondition] = useState<Record<string, unknown>>(variant.condition)
  const [styleValid, setStyleValid] = useState(true)
  const [conditionValid, setConditionValid] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Reset local state when a different variant is shown.
  useEffect(() => {
    setTemplate(variant.template)
    setDuration(String(variant.duration_ms))
    setStyle(variant.style)
    setCondition(variant.condition)
    setStyleValid(true)
    setConditionValid(true)
  }, [variant])

  const save = async () => {
    const ms = parseInt(duration, 10)
    if (Number.isNaN(ms) || ms < 0) {
      error('Duration must be a non-negative number of milliseconds.')
      return
    }
    if (!styleValid || !conditionValid) {
      error('Fix the invalid JSON before saving.')
      return
    }
    setSaving(true)
    try {
      const updated = await updateVariant(variant.overlay_id, variant.id, {
        template,
        duration_ms: ms,
        style,
        condition,
      })
      onSaved(updated)
      success(`Saved ${variant.alert_type} / ${variant.variant}`)
    } catch (err) {
      error(apiErrorMessage(err, 'Failed to save variant'))
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!confirm(`Delete the "${variant.variant}" ${variant.alert_type} variant?`)) return
    setDeleting(true)
    try {
      await deleteVariant(variant.overlay_id, variant.id)
      onDeleted(variant.id)
      success('Variant deleted')
    } catch (err) {
      error(apiErrorMessage(err, 'Failed to delete variant'))
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-4">
      <Field
        label="Template"
        value={template}
        onChange={setTemplate}
        placeholder="{username} just followed!"
        hint="Use {field} placeholders: {username}, {bits}, {viewers}, {months}, {tier}."
      />
      <Field
        label="Duration (ms)"
        type="number"
        value={duration}
        onChange={setDuration}
        hint="How long the alert is shown."
      />
      <JsonField
        label="Style overrides"
        value={style}
        onChange={(v, valid) => {
          setStyle(v)
          setStyleValid(valid)
        }}
        hint='e.g. {"text_color": "#fff", "accent_color": "#ff4fa3"}'
      />
      <JsonField
        label="Match condition"
        value={condition}
        onChange={(v, valid) => {
          setCondition(v)
          setConditionValid(valid)
        }}
        hint='When this variant applies, e.g. {"min_bits": 1000} or {"tier": "3000"}. Empty = always.'
      />

      <div className="flex items-center justify-between pt-2">
        <button
          onClick={remove}
          className="text-text-secondary hover:text-state-danger flex items-center gap-2 text-sm"
          disabled={deleting}
        >
          {deleting ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
          Delete variant
        </button>
        <button onClick={save} className="btn-primary flex items-center gap-2" disabled={saving}>
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
          Save variant
        </button>
      </div>
    </div>
  )
}

export default function OverlayEditor() {
  const navigate = useNavigate()
  const { channelId, overlayId } = useParams<{ channelId: string; overlayId: string }>()
  const chId = Number(channelId)

  const [overlay, setOverlay] = useState<Overlay | null>(null)
  const [variants, setVariants] = useState<AlertVariant[]>([])
  const [activeType, setActiveType] = useState<string>('follow')
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [savingOverlay, setSavingOverlay] = useState(false)
  const [firing, setFiring] = useState(false)

  const { success, error } = useToast()

  const load = useCallback(async () => {
    if (!overlayId) return
    try {
      const [ov, vs] = await Promise.all([
        getOverlay(overlayId),
        listVariants(overlayId),
      ])
      setOverlay(ov)
      setName(ov.name)
      setEnabled(ov.enabled)
      setVariants(vs)
      // Default the active tab to the first alert type that has a variant.
      const present = vs.map((v) => v.alert_type)
      if (present.length > 0) setActiveType(present[0])
      setLoadError(null)
    } catch (err) {
      // Fatal load failure stays INLINE, not a toast.
      setLoadError(apiErrorMessage(err, 'Failed to load overlay'))
    } finally {
      setIsLoading(false)
    }
  }, [overlayId])

  useEffect(() => {
    load()
  }, [load])

  const saveOverlay = async () => {
    if (!overlay) return
    setSavingOverlay(true)
    try {
      const updated = await updateOverlay(overlay.id, { name, enabled })
      setOverlay(updated)
      success('Overlay saved')
    } catch (err) {
      error(apiErrorMessage(err, 'Failed to save overlay'))
    } finally {
      setSavingOverlay(false)
    }
  }

  const addVariant = async (alertType: string) => {
    if (!overlay) return
    try {
      const created = await createVariant(overlay.id, {
        alert_type: alertType,
        variant: 'default',
        template: '{username}',
        duration_ms: 6000,
      })
      setVariants((prev) => [...prev, created])
      setActiveType(alertType)
      success(`Added ${alertType} variant`)
    } catch (err) {
      error(apiErrorMessage(err, 'Failed to add variant'))
    }
  }

  const onVariantSaved = (updated: AlertVariant) => {
    setVariants((prev) => prev.map((v) => (v.id === updated.id ? updated : v)))
  }

  const onVariantDeleted = (id: number) => {
    setVariants((prev) => prev.filter((v) => v.id !== id))
  }

  const fire = async () => {
    if (!overlay) return
    const preset = TEST_PRESETS[activeType] ?? TEST_PRESETS.follow
    setFiring(true)
    try {
      const result = await testFire(overlay.id, preset.capability, preset.fields)
      if (result.status === 'fired') {
        success('Test fired — check your overlay / preview')
      } else {
        error(result.detail || 'No overlay rendered for that event')
      }
    } catch (err) {
      error(apiErrorMessage(err, 'Test-fire failed'))
    } finally {
      setFiring(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-synth-magenta" />
      </div>
    )
  }

  if (loadError || !overlay) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(`/channels/${chId}/overlays`)}
          className="text-sm text-text-secondary hover:text-text-primary"
        >
          ← Back to overlays
        </button>
        <div className="bg-state-danger-bg border border-state-danger rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-state-danger" size={20} />
          <span className="text-state-danger">{loadError || 'Overlay not found'}</span>
        </div>
      </div>
    )
  }

  // Variants grouped by alert type; the active tab shows that type's variants.
  const typesWithVariants = ALERT_TYPES.filter((t) =>
    variants.some((v) => v.alert_type === t),
  )
  const activeVariants = variants.filter((v) => v.alert_type === activeType)
  const activeTypeHasVariant = typesWithVariants.includes(activeType as never)

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate(`/channels/${chId}/overlays`)}
          className="text-sm text-text-secondary hover:text-text-primary mb-2"
        >
          ← Back to overlays
        </button>
        <h2 className="text-2xl font-bold text-text-primary">{overlay.name}</h2>
        <p className="text-text-secondary">
          {overlay.overlay_type} overlay · slot {overlay.slot}
        </p>
      </div>

      {/* Overlay-level settings */}
      <div className="card space-y-4">
        <h3 className="font-semibold text-text-primary">Overlay settings</h3>
        <Field label="Name" value={name} onChange={setName} />
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 accent-synth-magenta"
          />
          <span className="text-sm text-text-secondary">Enabled</span>
        </label>
        <div className="flex justify-end">
          <button onClick={saveOverlay} className="btn-primary flex items-center gap-2" disabled={savingOverlay}>
            {savingOverlay ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            Save settings
          </button>
        </div>
      </div>

      {/* Alert variants */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-text-primary">Alert variants</h3>
          <button onClick={fire} className="btn-secondary flex items-center gap-2" disabled={firing}>
            {firing ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />}
            Test fire {activeType}
          </button>
        </div>

        {/* Alert-type tabs */}
        <div className="flex flex-wrap gap-2 border-b border-border pb-3">
          {ALERT_TYPES.map((t) => {
            const has = typesWithVariants.includes(t)
            return (
              <button
                key={t}
                onClick={() => setActiveType(t)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm transition-colors',
                  activeType === t
                    ? 'bg-synth-magenta text-text-on-accent'
                    : 'bg-surface-overlay text-text-secondary hover:bg-neutral-600',
                  !has && 'opacity-60',
                )}
              >
                {t}
                {!has && <span className="ml-1 text-xs">+</span>}
              </button>
            )
          })}
        </div>

        {/* Active type's variants, or an add prompt */}
        {activeTypeHasVariant ? (
          <div className="space-y-6">
            {activeVariants.map((v) => (
              <div key={v.id} className="border-t border-border pt-4 first:border-t-0 first:pt-0">
                <div className="text-xs uppercase tracking-wide text-text-muted mb-3">
                  variant: {v.variant}
                </div>
                <VariantEditor
                  variant={v}
                  onSaved={onVariantSaved}
                  onDeleted={onVariantDeleted}
                />
              </div>
            ))}
            <div className="border-t border-border pt-4">
              <button
                onClick={() => addVariant(activeType)}
                className="text-sm text-synth-magenta hover:text-synth-cyan flex items-center gap-2"
              >
                <Plus size={16} />
                Add another {activeType} variant
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-text-secondary mb-4">No {activeType} variant yet.</p>
            <button onClick={() => addVariant(activeType)} className="btn-primary inline-flex items-center gap-2">
              <Plus size={16} />
              Add {activeType} variant
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
